import { supabase } from '../supabase';
import { supabaseAdmin } from './supabase-admin';
import { v4 as uuidv4 } from 'uuid';

// Cache-busting comment - Updated 2025-06-19 01:03
// Supabase Storage service with bucket-based storage

// Media type identifiers (unique string keys)
export const MEDIA_TYPES = {
  AVATAR: 'avatar',
  COVER: 'cover',
  POST_IMAGE: 'post_image',
  POST_VIDEO: 'post_video',
  POST_AUDIO: 'post_audio',
  STORY: 'story',
  STORY_VIDEO: 'story_video',
  MESSAGE_MEDIA: 'message_media',
  MESSAGE_VIDEO: 'message_video',
  PRODUCT: 'product',
  GENERAL: 'general'
} as const;

export type MediaType = typeof MEDIA_TYPES[keyof typeof MEDIA_TYPES];

// Map media types to specific bucket names
export const MEDIA_TYPE_TO_BUCKET: Record<MediaType, string> = {
  [MEDIA_TYPES.AVATAR]: 'mozo_avatars',
  [MEDIA_TYPES.COVER]: 'mozo_avatars', // Using avatars bucket for covers too
  [MEDIA_TYPES.POST_IMAGE]: 'mozo_posts',
  [MEDIA_TYPES.POST_VIDEO]: 'mozo_posts',
  [MEDIA_TYPES.POST_AUDIO]: 'mozo_posts',
  [MEDIA_TYPES.STORY]: 'mozo_stories',
  [MEDIA_TYPES.STORY_VIDEO]: 'mozo_stories',
  [MEDIA_TYPES.MESSAGE_MEDIA]: 'mozo_media',
  [MEDIA_TYPES.MESSAGE_VIDEO]: 'mozo_media',
  [MEDIA_TYPES.PRODUCT]: 'mozo_products',
  [MEDIA_TYPES.GENERAL]: 'mozo_uploads'
};

/**
 * Helper function to determine the correct media type based on file type and context
 */
export const getMediaTypeForFile = (file: File, context: 'avatar' | 'post' | 'story' | 'message' | 'cover' | 'product'): MediaType => {
  const fileType = file.type;
  
  switch (context) {
    case 'avatar':
      return MEDIA_TYPES.AVATAR;
    
    case 'post':
      if (fileType.startsWith('image/')) return MEDIA_TYPES.POST_IMAGE;
      if (fileType.startsWith('video/')) return MEDIA_TYPES.POST_VIDEO;
      if (fileType.startsWith('audio/')) return MEDIA_TYPES.POST_AUDIO;
      return MEDIA_TYPES.POST_IMAGE; // Default to image folder
    
    case 'story':
      if (fileType.startsWith('image/')) return MEDIA_TYPES.STORY;
      if (fileType.startsWith('video/')) return MEDIA_TYPES.STORY_VIDEO;
      return MEDIA_TYPES.STORY; // Default to photo folder
    
    case 'message':
      if (fileType.startsWith('image/')) return MEDIA_TYPES.MESSAGE_MEDIA;
      if (fileType.startsWith('video/')) return MEDIA_TYPES.MESSAGE_VIDEO;
      return MEDIA_TYPES.MESSAGE_MEDIA; // Default to photo folder
    
    case 'cover':
      return MEDIA_TYPES.COVER;
      
    case 'product':
      return MEDIA_TYPES.PRODUCT;
    
    default:
      return MEDIA_TYPES.GENERAL; // Fallback
  }
};

// File size limits (in bytes)
export const FILE_SIZE_LIMITS: Record<MediaType, number> = {
  [MEDIA_TYPES.AVATAR]: 5 * 1024 * 1024, // 5MB
  [MEDIA_TYPES.COVER]: 10 * 1024 * 1024, // 10MB
  [MEDIA_TYPES.POST_IMAGE]: 10 * 1024 * 1024, // 10MB
  [MEDIA_TYPES.POST_VIDEO]: 100 * 1024 * 1024, // 100MB
  [MEDIA_TYPES.POST_AUDIO]: 50 * 1024 * 1024, // 50MB
  [MEDIA_TYPES.STORY]: 20 * 1024 * 1024, // 20MB
  [MEDIA_TYPES.STORY_VIDEO]: 50 * 1024 * 1024, // 50MB
  [MEDIA_TYPES.MESSAGE_MEDIA]: 25 * 1024 * 1024, // 25MB
  [MEDIA_TYPES.MESSAGE_VIDEO]: 50 * 1024 * 1024, // 50MB
  [MEDIA_TYPES.PRODUCT]: 15 * 1024 * 1024, // 15MB
  [MEDIA_TYPES.GENERAL]: 50 * 1024 * 1024, // 50MB
};

// Allowed file types
export const ALLOWED_FILE_TYPES: Record<MediaType, string[]> = {
  [MEDIA_TYPES.AVATAR]: ['image/jpeg', 'image/png', 'image/webp'],
  [MEDIA_TYPES.COVER]: ['image/jpeg', 'image/png', 'image/webp'],
  [MEDIA_TYPES.POST_IMAGE]: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
  [MEDIA_TYPES.POST_VIDEO]: ['video/mp4', 'video/webm', 'video/quicktime'],
  [MEDIA_TYPES.POST_AUDIO]: ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4'],
  [MEDIA_TYPES.STORY]: ['image/jpeg', 'image/png', 'image/webp', 'video/mp4'],
  [MEDIA_TYPES.STORY_VIDEO]: ['video/mp4', 'video/webm', 'video/quicktime'],
  [MEDIA_TYPES.MESSAGE_MEDIA]: ['image/jpeg', 'image/png', 'image/webp', 'video/mp4', 'audio/mpeg'],
  [MEDIA_TYPES.MESSAGE_VIDEO]: ['video/mp4', 'video/webm', 'video/quicktime'],
  [MEDIA_TYPES.PRODUCT]: ['image/jpeg', 'image/png', 'image/webp'],
  [MEDIA_TYPES.GENERAL]: ['image/jpeg', 'image/png', 'image/webp', 'video/mp4', 'audio/mpeg', 'application/pdf'],
};

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface UploadResult {
  success: boolean;
  url?: string;
  key?: string;
  error?: string;
  bucket?: string;
}

class SupabaseStorageService {
  /**
   * Validates file before upload
   */
  validateFile(file: File, mediaType: MediaType): { valid: boolean; error?: string } {
    // Validate that we have a valid file
    if (!file) {
      return {
        valid: false,
        error: 'No file provided'
      };
    }

    // Validate that we have a valid media type
    if (!Object.values(MEDIA_TYPES).includes(mediaType)) {
      console.warn(`Unknown media type: ${mediaType}, using GENERAL type for validation`);
      mediaType = MEDIA_TYPES.GENERAL;
    }
    
    // Check file size
    const maxSize = FILE_SIZE_LIMITS[mediaType] || (50 * 1024 * 1024); // Default to 50MB if not specified
    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File size exceeds the maximum allowed (${Math.round(maxSize / (1024 * 1024))}MB)`
      };
    }
    
    // Check file type
    const allowedTypes = ALLOWED_FILE_TYPES[mediaType] || [
      'image/jpeg', 'image/png', 'image/webp', 'video/mp4', 'audio/mpeg'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `File type ${file.type} is not allowed. Allowed types: ${allowedTypes.join(', ')}`
      };
    }
    
    return { valid: true };
  }

  /**
   * Generates a unique filename with timestamp and random string
   */
  generateFileName(originalName: string, userId?: string): string {
    const timestamp = new Date().getTime();
    const userPrefix = userId ? `${userId.slice(0, 8)}_` : '';
    const randomStr = uuidv4().slice(0, 8);
    const fileExt = originalName.split('.').pop() || '';
    
    return `${userPrefix}${timestamp}_${randomStr}.${fileExt}`;
  }

  /**
   * Optimizes image before upload (basic client-side optimization)
   */
  async optimizeImage(file: File, maxWidth: number = 1920, quality: number = 0.8): Promise<File> {
    // Only optimize images
    if (!file.type.startsWith('image/')) {
      return file;
    }
    
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        
        img.onload = () => {
          // Only resize if image is larger than maxWidth
          if (img.width <= maxWidth) {
            resolve(file);
            return;
          }
          
          const canvas = document.createElement('canvas');
          const ratio = maxWidth / img.width;
          canvas.width = maxWidth;
          canvas.height = img.height * ratio;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(file); // Fallback to original if canvas context fails
            return;
          }
          
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          // Convert to blob
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                resolve(file); // Fallback to original if blob creation fails
                return;
              }
              
              // Create new file from blob
              const optimizedFile = new File(
                [blob],
                file.name,
                { type: file.type, lastModified: file.lastModified }
              );
              
              console.log(`Image optimized: ${file.size} -> ${optimizedFile.size} bytes`);
              resolve(optimizedFile);
            },
            file.type,
            quality
          );
        };
        
        img.onerror = () => {
          resolve(file); // Fallback to original if image loading fails
        };
      };
      
      reader.onerror = () => {
        resolve(file); // Fallback to original if reading fails
      };
    });
  }
  async uploadFile(
    file: File, 
    mediaType: MediaType, 
    userId: string = '',
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResult> {
    try {
      // Validate file
      const validationResult = this.validateFile(file, mediaType);
      if (!validationResult.valid) {
        return { success: false, error: validationResult.error };
      }

      // Optimize image if it's an image file
      let fileToUpload = file;
      if (file.type.startsWith('image/')) {
        try {
          fileToUpload = await this.optimizeImage(file);
        } catch (err) {
          console.warn('Image optimization failed, using original file', err);
          // Continue with original file if optimization fails
          fileToUpload = file;
        }
      }

      // Create folder structure: year/month/
      const date = new Date();
      const year = date.getFullYear().toString();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const folderPath = `${year}/${month}/`;
      
      // Generate unique filename with user prefix if provided
      const fileExt = fileToUpload.name.split('.').pop() || '';
      const userPrefix = userId ? `${userId.slice(0, 8)}_` : '';
      const uniqueFileName = `${userPrefix}${uuidv4()}.${fileExt}`;
      const filePath = `${folderPath}${uniqueFileName}`;
      
      // Get the correct bucket for this media type
      const bucket = MEDIA_TYPE_TO_BUCKET[mediaType];
      
      // Try to upload with regular client first
      let data, error;
      
      try {
        const result = await supabase.storage
          .from(bucket)
          .upload(filePath, fileToUpload, {
            cacheControl: '3600',
            upsert: false
          });
          
        data = result.data;
        error = result.error;
        
        // If we get an RLS policy error, try with admin client
        if (error && (error.message?.includes('row-level security') || 
            (error as any).statusCode === '403' || 
            error.message?.includes('Unauthorized'))) {
          console.log('Retrying upload with admin client due to RLS policy error');
          
          const adminResult = await supabaseAdmin.storage
            .from(bucket)
            .upload(filePath, fileToUpload, {
              cacheControl: '3600',
              upsert: false
            });
            
          data = adminResult.data;
          error = adminResult.error;
        }
      } catch (uploadError) {
        console.error('Error during upload attempt:', uploadError);
        error = {
          message: uploadError instanceof Error ? uploadError.message : 'Unknown upload error'
        };
      }
      
      if (error) {
        console.error('Error uploading file to Supabase:', error);
        return {
          success: false,
          error: error.message
        };
      }
      
      // Get the public URL
      let publicURL: string | undefined;
      if (data) {
        publicURL = supabase.storage.from(bucket).getPublicUrl(data.path).data.publicUrl;
      }
      
      // Final progress update
      if (onProgress) {
        onProgress({
          loaded: fileToUpload.size,
          total: fileToUpload.size,
          percentage: 100
        });
      }
      
      if (!data) {
        return {
          success: false,
          error: 'Upload succeeded but no data was returned'
        };
      }
      
      return {
        success: true,
        key: data.path,
        url: publicURL,
        bucket
      };
    } catch (error) {
      console.error('[Supabase Storage] Upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed'
      };
    }
  }

  /**
   * Deletes a file from Supabase Storage
   */
  async deleteFile(key: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Extract bucket and path from key
      const [bucket, ...pathParts] = key.split('/');
      const path = pathParts.join('/');
      
      // Validate that the bucket exists in our mapping
      const bucketExists = Object.values(MEDIA_TYPE_TO_BUCKET).includes(bucket);
      if (!bucketExists) {
        return {
          success: false,
          error: `Invalid bucket: ${bucket}`
        };
      }
      
      const { error } = await supabase.storage
        .from(bucket)
        .remove([path]);
      
      if (error) {
        console.error('[Supabase Storage] Delete error:', error);
        return {
          success: false,
          error: error.message
        };
      }
      
      return { success: true };
    } catch (error) {
      console.error('[Supabase Storage] Delete error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Delete failed'
      };
    }
  }

  /**
   * Gets a publicly accessible URL for a file
   */
  getPublicUrl(key: string): string {
    try {
      // Extract bucket and path from key
      const [bucket, ...pathParts] = key.split('/');
      const path = pathParts.join('/');
      
      // Validate that the bucket exists in our mapping
      const bucketExists = Object.values(MEDIA_TYPE_TO_BUCKET).includes(bucket);
      if (!bucketExists) {
        console.error(`[Supabase Storage] Invalid bucket: ${bucket}`);
        return '';
      }
      
      const { data } = supabase.storage
        .from(bucket)
        .getPublicUrl(path);
      
      return data.publicUrl;
    } catch (error) {
      console.error('[Supabase Storage] Error getting public URL:', error);
      return '';
    }
  }

  /**
   * Gets a file preview with optimized size for mobile
   */
  getFilePreview(key: string, width = 400): string {
    try {
      // Extract bucket and path from key
      const [bucket, ...pathParts] = key.split('/');
      const path = pathParts.join('/');
      
      // Validate that the bucket exists in our mapping
      const bucketExists = Object.values(MEDIA_TYPE_TO_BUCKET).includes(bucket);
      if (!bucketExists) {
        console.error(`[Supabase Storage] Invalid bucket: ${bucket}`);
        return '';
      }
      
      const { data } = supabase.storage
        .from(bucket)
        .getPublicUrl(path, {
          transform: {
            width,
            height: width,
            resize: 'cover'
          }
        });
      
      return data.publicUrl;
    } catch (error) {
      console.error('[Supabase Storage] Error getting file preview:', error);
      return '';
    }
  }

  /**
   * Extracts key from public URL
   */
  extractKeyFromUrl(url: string): string | null {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      
      // Find the bucket name from our mapping
      const bucketValues = Object.values(MEDIA_TYPE_TO_BUCKET);
      const bucketIndex = pathParts.findIndex(part => bucketValues.includes(part));
      
      if (bucketIndex === -1) return null;
      
      // Reconstruct the key with bucket and remaining path
      const bucket = pathParts[bucketIndex];
      const path = pathParts.slice(bucketIndex + 1).join('/');
      
      return `${bucket}/${path}`;
    } catch {
      return null;
    }
  }

  /**
   * Batch upload multiple files
   */
  async uploadMultipleFiles(
    files: File[],
    mediaType: MediaType,
    userId: string = '',
    onProgress?: (fileIndex: number, progress: UploadProgress) => void
  ): Promise<UploadResult[]> {
    const results: UploadResult[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const result = await this.uploadFile(
        file,
        mediaType,
        userId,
        onProgress ? (progress) => onProgress(i, progress) : undefined
      );
      results.push(result);
    }

    return results;
  }
}

// Export singleton instance
export const supabaseStorage = new SupabaseStorageService();
export default supabaseStorage;
